import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useGlobalContext } from 'GlobalContext';
import { useModal } from 'components/modals/ModalContext';
import { useAllowedRight } from 'services/permissions/permissionChecks';
import { saveData } from 'services/api/saveData';
import LinkRelatedOrganizationModal from 'components/modals/LinkRelatedOrganizationModal';
import IconButton from 'components/buttons/IconButton';
import Tooltip from 'components/alerts/Tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuilding, faPlus, faXmark } from '@fortawesome/free-solid-svg-icons';
import '../../style/scss/card.scss';

// A linked organization as the backend serializes it
interface RelatedOrganization {
    id: number;
    name: string;
}

interface RelatedOrganizationsCardProps {
    itemId: string;
    data?: any;
    loadStatus?: 'idle' | 'loading' | 'loaded';
    isEditable?: boolean;
    refetchData?: () => void;
}

/*
 * RelatedOrganizationsCard.tsx
 * Shows the parent organization and the daughter organizations of an
 * organization with links to their pages, and lets the user link or
 * unlink them.
 */
const RelatedOrganizationsCard: React.FC<RelatedOrganizationsCardProps> = ({
    itemId, data, loadStatus, isEditable = true, refetchData
}) => {
    const { t } = useTranslation();
    const { setFloatingAlert } = useGlobalContext();
    const { initializeModal } = useModal();
    const [userHasOnlyViewRights] = useState<boolean>(useAllowedRight('only_view'));

    // Get the parent and the daughters from the page data
    const parent: RelatedOrganization | null = data?.parent_organization ?? null;
    const daughters: RelatedOrganization[] = data?.daughter_organizations ?? [];
    const totalCount = daughters.length + (parent ? 1 : 0);
    const canEdit = isEditable && !userHasOnlyViewRights;

    // Open the modal linking a parent or daughter organization to this organization
    const openLinkModal = () => {
        initializeModal(
            <LinkRelatedOrganizationModal
                organizationId={parseInt(itemId)}
                hasParent={Boolean(parent)}
                refetchData={() => refetchData && refetchData()} />,
            { title: t('organization.general.link_related_organization_label'), modalSize: 'small' }
        );
    };

    // Clear the parent of the given organization, used to unlink the parent and the daughters
    const handleUnlink = async (organizationId: number) => {
        await saveData({ apiUrl: `patch_organization/${organizationId}`, method: 'patch', data: { parent_organization: null } });
        if (refetchData) refetchData();
        setFloatingAlert({ type: 'success' });
    };

    // Render one organization row with its role and a link to its page
    const renderRow = (organization: RelatedOrganization, roleLabel: string, unlinkId: number) => (
        <div key={`${roleLabel}-${organization.id}`} className='card-list-item related-organization-row'>
            <div>
                <span className='related-organization-icon'>
                    <FontAwesomeIcon icon={faBuilding} />
                </span>
                <Link className='card-link' to={`/contacts/organization/${organization.id}`}>
                    {organization.name}
                </Link>
                <span className='related-organization-role'>{t(roleLabel)}</span>
            </div>
            {canEdit && (
                <div className='unlink-icon tooltip-icon'>
                    <FontAwesomeIcon icon={faXmark} onClick={() => handleUnlink(unlinkId)} />
                    <Tooltip>{t('forms.unlink_label')}</Tooltip>
                </div>
            )}
        </div>
    );

    return (
        <div className='card linked-items-card related-organizations-card'>
            <div className='header'>
                <h6>
                    {t('organization.general.related_organizations_label')}
                    {totalCount > 0 && <span> ({totalCount})</span>}
                </h6>
                {canEdit && (
                    <IconButton
                        onClick={openLinkModal}
                        size='extra-small'
                        icon={<FontAwesomeIcon icon={faPlus} />}
                        tooltipText={t('organization.general.link_related_organization_label')} />
                )}
            </div>
            {loadStatus === 'loading' ? (
                <div className='loader'></div>
            ) : (
                <>
                    {parent && renderRow(parent, 'organization.general.parent_organization_role_label', parseInt(itemId))}
                    {daughters.map(daughter => renderRow(daughter, 'organization.general.daughter_organization_role_label', daughter.id))}
                    {totalCount === 0 && (
                        <p className='related-organizations-empty'>{t('organization.general.related_organizations_empty_label')}</p>
                    )}
                </>
            )}
        </div>
    );
};

export default RelatedOrganizationsCard;
